import { memo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Bookmark, Compass, Home, MapPin, User } from 'lucide-react';
import { motion } from 'framer-motion';
import useAuth from '../hooks/useAuth';

const MobileBottomNav = ({ token }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const { userId, decodedUser } = useAuth();
    const pathname = location.pathname;
    const isPartnerRoute = pathname.startsWith('/partner')
        && !pathname.startsWith('/partners')
        && !pathname.startsWith('/partner-offers');

    // Determine profile href based on auth state
    const profileHref = (() => {
        if (!userId) return isPartnerRoute ? '/partner/login' : '/login';
        const type = decodedUser?.type;
        if (type === 'partner') return '/partner';
        if (type === 'admin' || type === 'super_admin') return '/admin';
        return '/profile';
    })();

    // User initial for profile icon when logged in
    const userInitial = decodedUser?.name ? decodedUser.name.charAt(0).toUpperCase() : null;

    const navItems = [
        { label: 'Home', icon: Home, href: '/' },
        { label: 'Explore', icon: Compass, href: '/treks' },
        { label: 'Regions', icon: MapPin, href: '/regions' },
        { label: 'Saved', icon: Bookmark, href: '/saved' },
        { label: 'Profile', icon: User, href: profileHref },
    ];

    const isActive = (href) => {
        if (href === '/') return pathname === '/';
        if (href === profileHref && userId) {
            return pathname === '/profile' || pathname === '/partner' || pathname === '/admin' || pathname === '/edit-profile';
        }
        return pathname.startsWith(href);
    };

    const handleClick = (href) => {
        navigate(href);
    };

    return (
        <nav
            className="fixed bottom-0 left-0 right-0 z-[950] border-t border-gray-200 bg-white/95 backdrop-blur-xl pb-[env(safe-area-inset-bottom,0px)] lg:hidden"
            role="navigation"
            aria-label="Bottom navigation"
        >
            <div className="flex items-center justify-around py-1 px-2">
                {navItems.map((item) => {
                    const active = isActive(item.href);
                    const Icon = item.icon;
                    const isProfileItem = item.label === 'Profile';
                    return (
                        <button
                            key={item.href}
                            type="button"
                            onClick={() => handleClick(item.href)}
                            className="relative flex flex-col items-center justify-center gap-0.5 py-2 px-3 min-w-[56px] min-h-[48px] -my-1"
                            aria-current={active ? 'page' : undefined}
                            aria-label={item.label}
                        >
                            {active && (
                                <motion.div
                                    layoutId="bottom-nav-active"
                                    className="absolute -top-1 left-1/2 h-1 w-6 -translate-x-1/2 rounded-full bg-emerald-600"
                                    transition={{ type: 'spring', stiffness: 500, damping: 35 }}
                                />
                            )}
                            {isProfileItem && userInitial ? (
                                <span
                                    className={`flex items-center justify-center w-5 h-5 rounded-full text-[10px] font-bold ${active ? 'bg-emerald-600 text-white' : 'bg-gray-200 text-gray-500'}`}>
                                    {userInitial}
                                </span>
                            ) : (
                                <Icon
                                    size={20}
                                    className={active ? 'text-emerald-600' : 'text-gray-400'}
                                    aria-hidden="true"
                                />
                            )}
                            <span
                                className={`text-[10px] font-semibold leading-none ${active ? 'text-emerald-600' : 'text-gray-400'}`}>
                                {item.label}
                            </span>
                        </button>
                    );
                })}
            </div>
        </nav>
    );
};

export { MobileBottomNav };
export default memo(MobileBottomNav);
